import { inject, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ActivityEntityName, Attachment } from '@jms/model';
import { injectWorkBuddyHost } from '@workbuddy/services/host/workbuddy.host.service';
import { UploadAllowedType } from '@workbuddy/ui/fields/upload/model/enums';
import { WorkBuddyDialogService } from '@workbuddy/ui/services/workbuddy.dialog.service';
import { FilesDashboardComponent } from '../components/files.dashboard/files.dashboard.component';
import { AllowedPickType, FilesContext, FilesDisplayMode } from '../model/files.context';

/** The records whose files an evidence field may link: the nearest one that is present wins. */
export interface RelatedFilesOwner {
    readonly stage?: { readonly _id?: string; readonly name?: string } | null;
    readonly job?: { readonly _id?: string; readonly name?: string } | null;
    readonly opportunity?: { readonly _id?: string; readonly name?: string } | null;
    readonly timezone?: string;
}

/** Add related for an evidence upload, on the office and the portal alike. */
export interface RelatedFilesPicker {
    /** False when the host has no files of the owner to offer, so Add related stays hidden. */
    canPick(owner: RelatedFilesOwner): boolean;
    /** Resolves the picked attachments, or none when the picker is dismissed. */
    pick(owner: RelatedFilesOwner, allowedTypes: UploadAllowedType): Promise<Attachment[]>;
}

// The portal's Files route serves only the stage assigned to the subcontractor, as the stage Files tab does; the office keeps the legacy task editor's order.
export function injectRelatedFilesPicker(): RelatedFilesPicker {

    const injector = inject(Injector);
    const contractorPortal = injectWorkBuddyHost().isContractorPortal;

    return {
        canPick: owner => !!relatedFilesContext(owner, contractorPortal, UploadAllowedType.All),
        pick: async (owner, allowedTypes) => {

            const context = relatedFilesContext(owner, contractorPortal, allowedTypes);
            if (!context) return [];
            // The picker reads DynamicDialogConfig/Ref, which only this dialog service provides, and it is not provided at the root.
            // The dashboard takes a route's `data.context` before the dialog's; the hosting job route's own context sent the portal to `files/[object Object]/undefined` (WBS-12090).
            const providers = [WorkBuddyDialogService, { provide: ActivatedRoute, useValue: { snapshot: { data: {} } } }];
            const scope = Injector.create({ providers, parent: injector });
            const dialogs = scope.get(WorkBuddyDialogService);
            const dialogRef = dialogs.open(FilesDashboardComponent, { data: { context } });
            // The scope parents the picker's own injector, so it is freed only after the picker view is gone; a lazy view created later would hit a destroyed injector (NG0205).
            dialogs.dialogComponentRefMap.get(dialogRef).onDestroy(() => scope.destroy());
            const picked = await firstValueFrom(dialogRef.onClose);

            return Array.isArray(picked) ? picked : [];
        },
    };
}

function relatedFilesContext(owner: RelatedFilesOwner, contractorPortal: boolean, allowedTypes: UploadAllowedType): FilesContext | null {

    const candidates: [ActivityEntityName, RelatedFilesOwner['stage']][] = contractorPortal
        ? [[ActivityEntityName.WorkOrder, owner.stage]]
        : [[ActivityEntityName.Opportunity, owner.opportunity], [ActivityEntityName.WorkOrder, owner.stage], [ActivityEntityName.Job, owner.job]];
    const found = candidates.find(([, record]) => !!record?._id);
    if (!found) return null;
    const [entity, record] = found;

    return {
        entity,
        entityId: record._id,
        entityName: record.name ?? '',
        activityTags: [],
        displayMode: FilesDisplayMode.Picker,
        timezone: owner.timezone,
        readonly: contractorPortal,
        allowTypes: pickTypes(allowedTypes),
    };
}

function pickTypes(allowedTypes: UploadAllowedType): AllowedPickType[] | undefined {

    switch (allowedTypes) {
        case UploadAllowedType.Files:
            return [AllowedPickType.Files, AllowedPickType.Forms];
        case UploadAllowedType.Images:
            return [AllowedPickType.Images];
        default:
            return undefined;
    }
}
